import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { Feature } from 'geojson'
import {
  LucideCircleDotDashed,
  LucideLineSquiggle,
  LucideMousePointerSquareDashed,
  LucideTrash2,
  LucideWaypoints,
  LucideX,
} from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import { useMap } from 'react-map-gl'
import {
  aDrawMode,
  aDrawnFeature,
  aMapDrawIsOpen,
  aMapDrawRef,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { ControlTooltip } from '~/components'
import { Button } from '~/components/ui'
import { cn } from '~/utils'
import { MAP_DRAW_STYLES } from './drawStyles'
import {
  CircleMode,
  DirectModeOverride,
  FreehandMode,
  SimpleSelectModeOverride,
} from './modes'

type MapboxDrawEvent = {
  features: Feature[]
}

export default function DrawControl({ className }: { className?: string }) {
  const mapRef = useMap()
  const drawRef = useRef<MapboxDraw>()
  const setMapDrawRef = useSetAtom(aMapDrawRef)
  const user = useAtomValue(aUser)
  const [isDrawOpen, setIsDrawOpen] = useAtom(aMapDrawIsOpen)
  const [drawMode, setDrawMode] = useAtom(aDrawMode)
  const [drawnFeature, setDrawnFeature] = useAtom(aDrawnFeature)

  const onUpdate = useCallback((e: MapboxDrawEvent) => {
    if (e.features.length > 0 && e.features[0]) {
      setDrawnFeature(e.features[0])
    }
  }, [])

  const deleteFeature = useCallback(() => {
    setDrawnFeature(null)
    drawRef.current!.trash()
    drawRef.current!.deleteAll()
  }, [])

  useEffect(() => {
    if (!drawnFeature && drawRef.current) {
      deleteFeature()
    }
  }, [drawnFeature, drawRef.current])

  useEffect(() => {
    if (!mapRef.current || drawRef.current) {
      return
    }

    const mapboxDraw = new MapboxDraw({
      displayControlsDefault: false,
      styles: MAP_DRAW_STYLES,
      userProperties: true,
      modes: {
        ...MapboxDraw.modes,
        draw_circle: CircleMode,
        draw_freehand: FreehandMode,
        direct_select: DirectModeOverride,
        simple_select: SimpleSelectModeOverride,
      },
    })
    drawRef.current = mapboxDraw
    setMapDrawRef(mapboxDraw)
    mapRef.current!.addControl(drawRef.current!)
    mapRef.current!.on('draw.create', onUpdate)
    mapRef.current!.on('draw.update', onUpdate)
    mapRef.current!.on('draw.selectionchange', (e: MapboxDrawEvent) => {
      if (e.features.length > 0) {
        setDrawMode('simple_select')
      } else {
        setDrawMode(null)
      }
    })

    if (drawnFeature) {
      drawRef.current!.add(drawnFeature)
      setIsDrawOpen(true)
    }
  }, [mapRef.current])

  const deleteIfSelected = useCallback(() => {
    const id = drawnFeature?.id?.toString()
    if (!drawRef.current || !id) {
      return
    }

    if (drawRef.current.getSelectedIds().includes(id)) {
      deleteFeature()
      setDrawMode(null)
    }
  }, [drawnFeature, deleteFeature, setDrawMode])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        deleteIfSelected()
      } else if (e.key === 'Escape' && drawMode) {
        deleteFeature()
        setDrawMode(drawMode)
        setTimeout(() => {
          drawRef.current!.changeMode(drawMode)
        }, 200)
      } else if (e.key === 'Enter' && drawMode && drawnFeature) {
        drawRef!.current!.changeMode('simple_select')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isDrawOpen, deleteIfSelected, drawMode, drawnFeature])

  if (!user) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md shadow-md ',
        isDrawOpen &&
          'bg-background/65 pr-2 backdrop-blur supports-[backdrop-filter]:bg-background/65',
        className
      )}>
      <ControlTooltip text="Start Drawing" side="bottom">
        <Button
          variant="map"
          size="map"
          className="w-auto px-3 shadow-none"
          onClick={() => {
            if (isDrawOpen) {
              if (!drawnFeature && drawMode === null) {
                setDrawMode(null)
                setIsDrawOpen(false)
              }
            } else {
              setIsDrawOpen(true)
            }
          }}>
          <LucideMousePointerSquareDashed className="h-5 w-5" />
          <span className="ml-2 hidden xl:inline-flex">Draw</span>
        </Button>
      </ControlTooltip>
      {isDrawOpen && (
        <>
          <ControlTooltip text="Draw Polygon" side="bottom">
            <Button
              variant="map"
              size="map"
              className={cn(
                'h-7 w-7 shadow-none',
                drawMode === 'draw_polygon' && 'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_polygon')
                setTimeout(() => {
                  drawRef.current!.changeMode('draw_polygon')
                }, 200)
              }}>
              <LucideWaypoints className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          <ControlTooltip text="Draw Circle" side="bottom">
            <Button
              variant="map"
              size="map"
              className={cn(
                'relative h-7 w-7 shadow-none',
                'hide-for-touch',
                drawMode === 'draw_circle' && 'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_circle')
                setTimeout(() => {
                  drawRef.current!.changeMode('draw_circle')
                }, 200)
              }}>
              <LucideCircleDotDashed className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          <ControlTooltip text="Draw Freehand Area" side="bottom">
            <Button
              variant="map"
              size="map"
              className={cn(
                'h-7 w-7 shadow-none',
                'hide-for-touch',
                drawMode === 'draw_freehand' && 'bg-primary text-white'
              )}
              onClick={() => {
                deleteFeature()
                setDrawMode('draw_freehand')
                setTimeout(() => {
                  drawRef.current!.changeMode('draw_freehand')
                }, 200)
              }}>
              <LucideLineSquiggle className="h-4 w-4" />
            </Button>
          </ControlTooltip>
          {drawnFeature && (
            <ControlTooltip text="Delete Drawing" side="bottom">
              <Button
                variant="map"
                size="map"
                className="h-7 w-7 shadow-none"
                onClick={() => {
                  deleteFeature()
                  setDrawMode(null)
                }}>
                <LucideTrash2 className="h-4 w-4" />
              </Button>
            </ControlTooltip>
          )}
          {drawMode && !drawnFeature && (
            <ControlTooltip text="Cancel Drawing" side="bottom">
              <Button
                variant="map"
                size="map"
                className="h-7 w-7 shadow-none"
                onClick={() => {
                  setDrawMode(null)
                  setTimeout(() => {
                    drawRef!.current!.trash()
                    drawRef!.current!.deleteAll()
                    drawRef!.current!.changeMode('simple_select')
                  }, 200)
                }}>
                <LucideX className="h-4 w-4" />
              </Button>
            </ControlTooltip>
          )}
        </>
      )}
    </div>
  )
}
